import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

interface Args {
  alertIds: readonly string[]
  /**
   * Opaque key describing the current result-set definition (filters +
   * sort). When it changes, keyboard focus is dropped: the row an operator
   * had focused no longer has a stable meaning in the new result set.
   */
  resetKey?: string
  onToggleSelect: (id: string, range: boolean) => void
  onToggleSelectAll: () => void
  onClearSelection: () => void
  onShowShortcuts: () => void
  onFocusSearch: () => void
}

/**
 * AF-126 — Queue-level keyboard navigation (j/k/Enter/space/x/?/Esc).
 *
 * KUP-API-003: focus is tracked by alert ID, NOT by row index. An index is
 * only a position in the list as rendered right now; a WebSocket insert,
 * refetch or re-sort shifts it, so an index-based Enter used to open a
 * different alert than the one the operator was looking at. The index
 * handed to the table is derived from the current list on every render,
 * and Enter / Space only act when the focused ID is present in the current
 * list — there is deliberately NO fallback to row 0. Clicking a row sets
 * the focused ID (see `setFocusedId`), so click-then-Enter opens the
 * clicked row.
 *
 * Returns the derived index (-1 when nothing owns keyboard focus). Skips
 * when the user is typing in an input.
 */
export function useQueueKeyboard({
  alertIds,
  resetKey = '',
  onToggleSelect,
  onToggleSelectAll,
  onClearSelection,
  onShowShortcuts,
  onFocusSearch,
}: Args) {
  const navigate = useNavigate()
  // Focus is scoped to a resetKey: a stale key means "focus belongs to a
  // previous result set" and is ignored without needing an effect.
  const [focus, setFocus] = useState<{ id: string | null; key: string }>({ id: null, key: resetKey })
  const candidateId = focus.key === resetKey ? focus.id : null
  const rawIndex = candidateId === null ? -1 : alertIds.indexOf(candidateId)
  const focusedId = rawIndex >= 0 ? candidateId : null
  const focusedIndex = rawIndex

  const setFocusedId = useCallback(
    (id: string | null) => setFocus({ id, key: resetKey }),
    [resetKey],
  )

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      // Don't hijack keys while the user is typing.
      const target = e.target instanceof HTMLElement ? e.target : null
      const tag = target?.tagName?.toLowerCase()
      if (tag === 'input' || tag === 'textarea' || target?.isContentEditable) {
        if (e.key === 'Escape' && tag === 'input') {
          ;(e.target as HTMLInputElement).blur()
        }
        return
      }
      // Don't hijack Enter / Space when the user has tabbed onto an
      // interactive element (anchor, button, role=button, link). The
      // queue shortcut should only fire while the user is navigating
      // rows, not while activating the skip link, sidebar links, or
      // toolbar buttons.
      if (
        tag === 'a' ||
        tag === 'button' ||
        target?.getAttribute('role') === 'button' ||
        target?.getAttribute('role') === 'link'
      ) {
        return
      }

      if (e.key === 'j' || e.key === 'k') {
        if (alertIds.length === 0) return
        e.preventDefault()
        const nextIndex =
          e.key === 'j'
            ? Math.min(alertIds.length - 1, focusedIndex + 1)
            : Math.max(0, focusedIndex < 0 ? 0 : focusedIndex - 1)
        const next = alertIds[nextIndex]
        if (next === undefined) return
        setFocusedId(next)
        if (e.shiftKey) onToggleSelect(next, true)
      } else if (e.key === 'Enter') {
        // Only act when the queue keyboard model owns focus.
        if (focusedId !== null) {
          e.preventDefault()
          void navigate(`/alerts/${focusedId}`)
        }
      } else if (e.key === ' ') {
        if (focusedId !== null) {
          e.preventDefault()
          onToggleSelect(focusedId, false)
        }
      } else if (e.key === 'x') {
        e.preventDefault()
        onToggleSelectAll()
      } else if (e.key === '/') {
        e.preventDefault()
        onFocusSearch()
      } else if (e.key === 'Escape') {
        onClearSelection()
      } else if (e.key === '?') {
        e.preventDefault()
        onShowShortcuts()
      }
    }

    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [
    alertIds,
    focusedId,
    focusedIndex,
    setFocusedId,
    navigate,
    onClearSelection,
    onFocusSearch,
    onShowShortcuts,
    onToggleSelect,
    onToggleSelectAll,
  ])

  return { focusedIndex, focusedId, setFocusedId }
}
